import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface SharedProject {
  id: string
  name: string
  type: 'collab' | 'team'
  owner_name?: string
  owner_email?: string
  my_role?: string
  teamId?: string
}

export function SharedProjectsView() {
  const [projects, setProjects] = useState<SharedProject[]>([])
  const [loading, setLoading] = useState(true)
  const [activeProject, setActiveProject] = useState<SharedProject | null>(null)
  const [previewFiles, setPreviewFiles] = useState<any[]>([])
  const [previewLoading, setPreviewLoading] = useState(false)
  const [running, setRunning] = useState(false)
  const [devPort, setDevPort] = useState<number | null>(null)
  const [starting, setStarting] = useState(false)
  const [editFile, setEditFile] = useState<{ path: string; name: string; content: string } | null>(null)
  const [editContent, setEditContent] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const fetchAll = async () => {
      const results: SharedProject[] = []
      try {
        const collabRes = await authFetch(apiUrl('/api/projects/shared'))
        if (collabRes.ok) {
          const data = await collabRes.json()
          if (Array.isArray(data)) {
            data.forEach((p: any) => results.push({
              id: p.id, name: p.name || p.slug, type: 'collab',
              owner_name: p.owner_name, owner_email: p.owner_email, my_role: p.my_role,
            }))
          }
        }
      } catch {}
      try {
        const teamsRes = await authFetch(apiUrl('/api/teams'))
        if (teamsRes.ok) {
          const teams = await teamsRes.json()
          const userId = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.user?.id
          if (Array.isArray(teams)) {
            const otherTeams = teams.filter((t: any) => t.owner_id !== userId)
            for (const team of otherTeams) {
              try {
                const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
                const res = await fetch(apiUrl('/api/files/list'), {
                  headers: { 'Authorization': `Bearer ${token}`, 'X-Team-Id': team.id },
                })
                if (res.ok) {
                  const tree = await res.json()
                  if (Array.isArray(tree)) {
                    tree.filter((n: any) => n.type === 'folder' && n.children?.length > 0 && !n.name.startsWith('.')).forEach((folder: any) => {
                      results.push({
                        id: `${team.id}:${folder.name}`,
                        name: folder.name,
                        type: 'team',
                        owner_name: team.name,
                        my_role: team.my_role || 'member',
                        teamId: team.id,
                      })
                    })
                  }
                }
              } catch {}
            }
          }
        }
      } catch {}
      setProjects(results)
      setLoading(false)
    }
    fetchAll()
  }, [])

  const canEdit = (p: SharedProject) => p.my_role === 'admin' || p.my_role === 'member' || p.my_role === 'editor' || p.my_role === 'owner'

  const getHeaders = (p: SharedProject): Record<string, string> => {
    const token = JSON.parse(localStorage.getItem('pulse-auth') ?? '{}')?.state?.token ?? ''
    const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` }
    if (p.teamId) headers['X-Team-Id'] = p.teamId
    return headers
  }

  const openProject = async (p: SharedProject) => {
    setActiveProject(p)
    setPreviewLoading(true)
    setPreviewFiles([])
    setDevPort(null)
    setRunning(false)
    setEditFile(null)
    setStarting(false)
    try {
      const res = await fetch(apiUrl('/api/files/list'), { headers: getHeaders(p) })
      if (res.ok) {
        const tree = await res.json()
        if (Array.isArray(tree)) {
          const folder = tree.find((n: any) => n.name === p.name && n.type === 'folder')
          setPreviewFiles(folder?.children || [])
        }
      }
    } catch {}
    setPreviewLoading(false)
  }

  const runProject = async (p: SharedProject) => {
    setStarting(true)
    try {
      await authFetch(apiUrl('/api/terminal/kill'), { method: 'POST' }).catch(() => {})
      await new Promise(r => setTimeout(r, 1500))

      const cmd = `cd ${p.name} && npm install && npx --yes vite --host --port 5175`
      await authFetch(apiUrl('/api/terminal/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: cmd, teamId: p.teamId || undefined }),
      })

      let attempts = 0
      const poll = setInterval(async () => {
        attempts++
        if (attempts > 30) { clearInterval(poll); setStarting(false); return }
        try {
          const r = await authFetch(apiUrl(`/api/devserver/status?_=${Date.now()}`))
          const s = await r.json()
          if (s.port === 5175) {
            setDevPort(5175)
            setRunning(true)
            setStarting(false)
            clearInterval(poll)
          }
        } catch {}
      }, 3000)
    } catch {
      setStarting(false)
    }
  }

  const stopProject = async () => {
    try {
      await authFetch(apiUrl('/api/terminal/run'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: '\x03' }),
      })
      await authFetch(apiUrl('/api/terminal/kill'), { method: 'POST' }).catch(() => {})
    } catch {}
    setRunning(false)
    setDevPort(null)
  }

  const openFile = async (p: SharedProject, filePath: string, fileName: string) => {
    try {
      const res = await fetch(apiUrl(`/api/files/read?path=${encodeURIComponent(filePath)}`), { headers: getHeaders(p) })
      if (res.ok) {
        const data = await res.json()
        const content = data.content ?? ''
        setEditFile({ path: filePath, name: fileName, content })
        setEditContent(content)
      }
    } catch {}
  }

  const saveFile = async () => {
    if (!editFile || !activeProject) return
    setSaving(true)
    try {
      await fetch(apiUrl('/api/files/write'), {
        method: 'POST',
        headers: { ...getHeaders(activeProject), 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: editFile.path, content: editContent }),
      })
      setEditFile({ ...editFile, content: editContent })
    } catch {}
    setSaving(false)
  }

  // ── Project IDE view (split layout) ──
  if (activeProject) {
    const editable = canEdit(activeProject)
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0, background: 'rgba(0,0,0,0.3)' }}>
          <button onClick={() => { setActiveProject(null); setDevPort(null); setRunning(false); setEditFile(null) }} style={backBtn}>←</button>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: 'ui-monospace, monospace' }}>{activeProject.name}</span>
          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontFamily: 'ui-monospace, monospace' }}>
            {activeProject.owner_name} · {activeProject.my_role}
          </span>
          <div style={{ flex: 1 }} />
          {!running && !starting && (
            <button onClick={() => runProject(activeProject)} style={runBtn}>▶ Run</button>
          )}
          {starting && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', width: 18, height: 18 }}>
                <div style={{ position: 'absolute', inset: 0, border: '2px solid rgba(255,255,255,0.1)', borderTopColor: '#5fd4a4', borderRadius: '50%', animation: 'shared-spin 0.8s linear infinite' }} />
              </div>
              <span style={{ fontSize: 10, color: '#5fd4a4', fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>Starting...</span>
              <style>{`@keyframes shared-spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )}
          {running && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 9, padding: '3px 8px', borderRadius: 4, background: 'rgba(95,212,164,0.15)', color: '#5fd4a4', fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>● RUNNING</span>
              <button onClick={() => stopProject()} style={stopBtn}>■ Stop</button>
            </div>
          )}
        </div>

        {/* Split: file tree left | editor/preview right */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left: File tree */}
          <div style={{ width: 220, borderRight: '1px solid rgba(255,255,255,0.06)', overflow: 'auto', flexShrink: 0, background: 'rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '8px 12px', fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'ui-monospace, monospace' }}>
              FILES
            </div>
            {previewLoading && <div style={{ padding: '8px 12px', fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>Loading...</div>}
            {!previewLoading && previewFiles.map((node: any) => (
              <FileTreeNode
                key={node.path}
                node={node}
                depth={0}
                activeFile={editFile?.path ?? null}
                onSelect={(path, name) => openFile(activeProject, path, name)}
              />
            ))}
          </div>

          {/* Right: Editor + Preview side by side */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', minWidth: 0 }}>
            {/* Editor (left half or full if no preview) */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: running && devPort ? '1px solid rgba(255,255,255,0.06)' : undefined }}>
              {editFile ? (
                <>
                  {/* File tab bar */}
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px', height: 32, background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: '#fff', fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{editFile.name}</span>
                    <div style={{ flex: 1 }} />
                    {editable && (
                      <button onClick={saveFile} disabled={saving} style={{ ...saveBtn, opacity: saving ? 0.5 : 1 }}>
                        {saving ? '...' : 'Save'}
                      </button>
                    )}
                  </div>
                  {/* Textarea / read-only viewer */}
                  {editable ? (
                    <textarea
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      spellCheck={false}
                      style={editorStyle}
                    />
                  ) : (
                    <pre style={{ ...editorStyle, margin: 0, overflow: 'auto' }}>{editFile.content}</pre>
                  )}
                </>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.25)', fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>
                  ← Select a file to {editable ? 'edit' : 'view'}
                </div>
              )}
            </div>

            {/* Preview (right half, only when running) */}
            {running && devPort && (
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <iframe
                  src={`http://localhost:${devPort}`}
                  style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ── Project cards grid ──
  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Loading...</div>
  }

  if (projects.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 6 }}>No shared projects yet</div>
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>
          When someone invites you to a team or collab session, their projects will appear here.
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px 32px' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 20 }}>Shared with you</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
        {projects.map((p) => (
          <div key={p.id} onClick={() => openProject(p)} style={{
            background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s', cursor: 'pointer',
          }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(124,110,245,0.3)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}
          >
            <div style={{ height: 70, background: 'rgba(255,255,255,0.015)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ fontSize: 24 }}>{p.type === 'team' ? '👥' : '📁'}</span>
            </div>
            <div style={{ padding: '10px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{p.name}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontFamily: 'ui-monospace, monospace', marginBottom: 6 }}>
                {p.owner_name || p.owner_email || 'Unknown'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 4, background: canEdit(p) ? 'rgba(95,212,164,0.12)' : 'rgba(255,255,255,0.05)', color: canEdit(p) ? '#5fd4a4' : 'rgba(255,255,255,0.3)', fontWeight: 700, fontFamily: 'ui-monospace, monospace' }}>
                  {canEdit(p) ? 'can edit' : 'view only'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── File tree node ──
function FileTreeNode({ node, depth, activeFile, onSelect }: {
  node: any; depth: number; activeFile: string | null;
  onSelect: (path: string, name: string) => void;
}) {
  const [open, setOpen] = useState(depth === 0)
  const isActive = node.path === activeFile

  return (
    <>
      <div
        onClick={() => { if (node.type === 'folder') setOpen(!open); else onSelect(node.path, node.name) }}
        style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '4px 8px',
          paddingLeft: 8 + depth * 12, cursor: 'pointer', fontSize: 11,
          fontFamily: 'ui-monospace, monospace',
          color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
          background: isActive ? 'rgba(124,110,245,0.15)' : 'none',
          borderLeft: isActive ? '2px solid #7c6ef5' : '2px solid transparent',
          transition: 'background 0.1s',
        }}
        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)' }}
        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'none' }}
      >
        <span style={{ width: 10, fontSize: 8, color: 'rgba(255,255,255,0.3)' }}>
          {node.type === 'folder' ? (open ? '▾' : '▸') : ''}
        </span>
        <span style={{ fontSize: 11 }}>{node.type === 'folder' ? '📁' : '◻'}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.name}</span>
      </div>
      {node.type === 'folder' && open && node.children?.map((child: any) => (
        <FileTreeNode key={child.path} node={child} depth={depth + 1} activeFile={activeFile} onSelect={onSelect} />
      ))}
    </>
  )
}

const editorStyle: React.CSSProperties = {
  flex: 1, width: '100%', padding: '12px 14px', background: 'rgba(0,0,0,0.4)',
  color: '#e0e0e0', fontFamily: 'ui-monospace, monospace', fontSize: 12,
  lineHeight: 1.7, resize: 'none', outline: 'none', border: 'none',
  whiteSpace: 'pre', overflowWrap: 'normal', overflow: 'auto',
}
const runBtn: React.CSSProperties = {
  padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(95,212,164,0.4)',
  background: 'rgba(95,212,164,0.1)', color: '#5fd4a4', fontSize: 10,
  cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700,
}
const saveBtn: React.CSSProperties = {
  padding: '3px 10px', borderRadius: 4, border: '1px solid rgba(124,110,245,0.3)',
  background: 'rgba(124,110,245,0.1)', color: '#7c6ef5', fontSize: 9,
  cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700,
}
const backBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
  cursor: 'pointer', fontSize: 14, padding: '2px 6px',
}
const stopBtn: React.CSSProperties = {
  padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(240,112,112,0.4)',
  background: 'rgba(240,112,112,0.1)', color: '#f07070', fontSize: 10,
  cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700,
}
